import React, { useState, useEffect } from 'react';
import CommonLayout from '../../../layouts/CommonLayout';
import { Modal, Button, Form, Badge, Tab, Tabs } from 'react-bootstrap';
import { allAPiServicesCall } from '../../../services/apiServices';
import { getAuthConfig } from '../../../services/apiUtils';
import './UserActivityList.css';
import { extractObjectId } from '../../../utils/utils';
import {
    FaUsers,
    FaSearch,
    FaTrophy,
    FaChevronLeft,
    FaChevronRight,
    FaEye,
    FaComment,
    FaStar,
    FaHandsHelping,
    FaChartLine,
    FaCalendarAlt,
    FaCoins,
    FaMedal,
    FaCrown
} from 'react-icons/fa';

const UserActivityList = () => {
    const [users, setUsers] = useState([]);
    const [leaderboard, setLeaderboard] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({});
    const [selectedUser, setSelectedUser] = useState(null);
    const [userActivity, setUserActivity] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('users');
    const [modalTab, setModalTab] = useState('comments');

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await allAPiServicesCall.getUsersActivity(page, 50, search, getAuthConfig());
            if (response?.status === 200) {
                setUsers(response.data.users);
                setPagination(response.data.pagination);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchLeaderboard = async () => {
        try {
            const response = await allAPiServicesCall.getActivityLeaderboard(20, getAuthConfig());
            if (response?.status === 200) {
                setLeaderboard(response.data.leaderboard);
            }
        } catch (error) {
            console.error("Error fetching leaderboard:", error);
        }
    };

    const fetchUserDetail = async (userId) => {
        setLoading(true);
        try {
            const response = await allAPiServicesCall.getUserActivityDetail(userId, getAuthConfig());
            if (response?.status === 200) {
                setUserActivity(response.data);
                setModalOpen(true);
            }
        } catch (error) {
            console.error("Error fetching user detail:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchLeaderboard();
    }, [page]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        fetchUsers();
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const getInitials = (name) => {
        if (!name) return '?';
        return name.charAt(0).toUpperCase();
    };

    return (
        <CommonLayout>
            <div className="user-activity-page">
                {/* Header Section */}
                <div className="user-activity-header">
                    <div className="user-activity-header-top">
                        <div className="user-activity-title-wrapper">
                            <div className="user-activity-title-icon">
                                <FaUsers />
                            </div>
                            <div className="user-activity-title-text">
                                <h1 className="user-activity-title">User Activity Tracking</h1>
                                <p className="user-activity-subtitle">Track user comments, reviews, and contributions</p>
                            </div>
                        </div>
                        <div className="user-activity-stats">
                            <div className="user-activity-stat-badge">
                                <FaUsers />
                                <span>Total Users: <strong>{pagination.total || 0}</strong></span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs Section */}
                <div className="user-activity-tabs-wrapper">
                    <div className="user-activity-tabs">
                        <button
                            className={`user-activity-tab ${activeTab === 'users' ? 'active' : ''}`}
                            onClick={() => setActiveTab('users')}
                        >
                            <FaUsers /> All Users
                        </button>
                        <button
                            className={`user-activity-tab ${activeTab === 'leaderboard' ? 'active' : ''}`}
                            onClick={() => setActiveTab('leaderboard')}
                        >
                            <FaTrophy /> Activity Leaderboard
                        </button>
                    </div>
                </div>

                {activeTab === 'users' && (
                    <>
                        {/* Search Bar */}
                        <form onSubmit={handleSearch} className="user-activity-search-bar">
                            <div className="user-activity-search-wrapper">
                                <FaSearch className="user-activity-search-icon" />
                                <input
                                    type="text"
                                    className="user-activity-search-input"
                                    placeholder="Search by username or email..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                            <button type="submit" className="user-activity-search-btn">
                                <FaSearch /> Search
                            </button>
                        </form>

                        {/* Main Card */}
                        <div className="user-activity-card">
                            {loading ? (
                                <div className="user-activity-loading">
                                    <div className="user-activity-loading-spinner"></div>
                                    <span className="user-activity-loading-text">Loading users...</span>
                                </div>
                            ) : users.length === 0 ? (
                                <div className="user-activity-empty">
                                    <div className="user-activity-empty-icon">
                                        <FaUsers />
                                    </div>
                                    <span className="user-activity-empty-text">No users found</span>
                                    <span className="user-activity-empty-subtitle">
                                        Try adjusting your search criteria.
                                    </span>
                                </div>
                            ) : (
                                <>
                                    {/* Desktop Table View */}
                                    <div className="user-activity-table-container">
                                        <table className="user-activity-table">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>User</th>
                                                    <th>Email</th>
                                                    <th>Points</th>
                                                    <th>Comments</th>
                                                    <th>Reviews</th>
                                                    <th>Contributions</th>
                                                    <th>Total</th>
                                                    <th>Joined</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {users.map((user, index) => (
                                                    <tr key={user._id}>
                                                        <td>
                                                            <span className="user-activity-id">
                                                                {(page - 1) * 50 + index + 1}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="user-activity-user-cell">
                                                                {user.avatar ? (
                                                                    <img
                                                                        src={user.avatar}
                                                                        alt={user.username}
                                                                        className="user-activity-avatar"
                                                                    />
                                                                ) : (
                                                                    <div className="user-activity-avatar-placeholder">
                                                                        {getInitials(user.username)}
                                                                    </div>
                                                                )}
                                                                <span className="user-activity-username">{user.username}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className="user-activity-email">{user.email}</span>
                                                        </td>
                                                        <td>
                                                            <span className="user-activity-badge points">{user.points}</span>
                                                        </td>
                                                        <td>
                                                            <span className="user-activity-badge comments">{user.activity.comments}</span>
                                                        </td>
                                                        <td>
                                                            <span className="user-activity-badge reviews">{user.activity.reviews}</span>
                                                        </td>
                                                        <td>
                                                            <span className="user-activity-badge contributions">{user.activity.contributions}</span>
                                                        </td>
                                                        <td>
                                                            <span className="user-activity-badge total">{user.activity.total}</span>
                                                        </td>
                                                        <td>{formatDate(user.createdAt)}</td>
                                                        <td>
                                                            <button
                                                                className="user-activity-btn-view"
                                                                onClick={() => {
                                                                    setSelectedUser(user);
                                                                    fetchUserDetail(extractObjectId(user._id));
                                                                }}
                                                            >
                                                                <FaEye /> View
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Mobile Card View */}
                                    <div className="user-activity-mobile-view">
                                        {users.map((user, index) => (
                                            <div key={user._id} className="user-activity-mobile-card">
                                                <div className="user-activity-mobile-card-header">
                                                    {user.avatar ? (
                                                        <img
                                                            src={user.avatar}
                                                            alt={user.username}
                                                            className="user-activity-avatar"
                                                        />
                                                    ) : (
                                                        <div className="user-activity-avatar-placeholder">
                                                            {getInitials(user.username)}
                                                        </div>
                                                    )}
                                                    <div className="user-activity-mobile-card-info">
                                                        <h3 className="user-activity-mobile-card-name">{user.username}</h3>
                                                        <span className="user-activity-mobile-card-email">{user.email}</span>
                                                    </div>
                                                </div>
                                                <div className="user-activity-mobile-card-stats">
                                                    <span className="user-activity-badge points">{user.points} pts</span>
                                                    <span className="user-activity-badge comments">{user.activity.comments} comments</span>
                                                    <span className="user-activity-badge reviews">{user.activity.reviews} reviews</span>
                                                    <span className="user-activity-badge contributions">{user.activity.contributions} contrib</span>
                                                </div>
                                                <div className="user-activity-mobile-card-actions">
                                                    <button
                                                        className="user-activity-btn-view"
                                                        onClick={() => {
                                                            setSelectedUser(user);
                                                            fetchUserDetail(extractObjectId(user._id));
                                                        }}
                                                    >
                                                        <FaEye /> View Details
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Pagination */}
                                    <div className="user-activity-pagination">
                                        <span className="user-activity-pagination-info">
                                            Showing {users.length} of {pagination.total} users
                                        </span>
                                        <div className="user-activity-pagination-controls">
                                            <button
                                                className="user-activity-pagination-btn"
                                                disabled={page === 1}
                                                onClick={() => setPage(p => p - 1)}
                                            >
                                                <FaChevronLeft /> Previous
                                            </button>
                                            <div className="user-activity-pagination-current">
                                                <span>Page</span>
                                                <strong>{page}</strong>
                                                <span>of</span>
                                                <strong>{pagination.totalPages}</strong>
                                            </div>
                                            <button
                                                className="user-activity-pagination-btn"
                                                disabled={page >= pagination.totalPages}
                                                onClick={() => setPage(p => p + 1)}
                                            >
                                                Next <FaChevronRight />
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </>
                )}

                {activeTab === 'leaderboard' && (
                    <>
                        <h4 className="user-activity-leaderboard-title">
                            <FaTrophy /> Top Contributors
                        </h4>
                        <div className="user-activity-card">
                            {leaderboard.length === 0 ? (
                                <div className="user-activity-empty">
                                    <div className="user-activity-empty-icon">
                                        <FaTrophy />
                                    </div>
                                    <span className="user-activity-empty-text">No activity data available</span>
                                    <span className="user-activity-empty-subtitle">
                                        Activity will appear once users start contributing.
                                    </span>
                                </div>
                            ) : (
                                <>
                                    {/* Desktop Table View */}
                                    <div className="user-activity-table-container">
                                        <table className="user-activity-table">
                                            <thead>
                                                <tr>
                                                    <th>Rank</th>
                                                    <th>User</th>
                                                    <th>Email</th>
                                                    <th>Comments</th>
                                                    <th>Reviews</th>
                                                    <th>Contributions</th>
                                                    <th>Total Activity</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {leaderboard.map((user, index) => (
                                                    <tr key={user._id} className={index < 3 ? 'top-rank' : ''}>
                                                        <td>
                                                            {index === 0 ? (
                                                                <span className="user-activity-rank gold"><FaCrown /></span>
                                                            ) : index === 1 ? (
                                                                <span className="user-activity-rank silver"><FaMedal /></span>
                                                            ) : index === 2 ? (
                                                                <span className="user-activity-rank bronze"><FaMedal /></span>
                                                            ) : (
                                                                <span className="user-activity-id">{index + 1}</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div className="user-activity-user-cell">
                                                                {user.avatar ? (
                                                                    <img
                                                                        src={user.avatar}
                                                                        alt={user.username}
                                                                        className="user-activity-avatar"
                                                                    />
                                                                ) : (
                                                                    <div className="user-activity-avatar-placeholder">
                                                                        {getInitials(user.username)}
                                                                    </div>
                                                                )}
                                                                <span className="user-activity-username">{user.username}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className="user-activity-email">{user.email}</span>
                                                        </td>
                                                        <td>
                                                            <span className="user-activity-badge comments">{user.commentsCount}</span>
                                                        </td>
                                                        <td>
                                                            <span className="user-activity-badge reviews">{user.reviewsCount}</span>
                                                        </td>
                                                        <td>
                                                            <span className="user-activity-badge contributions">{user.contributionsCount}</span>
                                                        </td>
                                                        <td>
                                                            <span className="user-activity-badge total">{user.totalActivity}</span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Mobile Card View */}
                                    <div className="user-activity-mobile-view">
                                        {leaderboard.map((user, index) => (
                                            <div key={user._id} className={`user-activity-mobile-card ${index < 3 ? 'top-rank' : ''}`}>
                                                <div className="user-activity-mobile-card-header">
                                                    {index === 0 ? (
                                                        <span className="user-activity-rank gold"><FaCrown /></span>
                                                    ) : index === 1 ? (
                                                        <span className="user-activity-rank silver"><FaMedal /></span>
                                                    ) : index === 2 ? (
                                                        <span className="user-activity-rank bronze"><FaMedal /></span>
                                                    ) : (
                                                        <span className="user-activity-id">{index + 1}</span>
                                                    )}
                                                    <div className="user-activity-mobile-card-info">
                                                        <h3 className="user-activity-mobile-card-name">{user.username}</h3>
                                                        <span className="user-activity-mobile-card-email">{user.email}</span>
                                                    </div>
                                                </div>
                                                <div className="user-activity-mobile-card-stats">
                                                    <span className="user-activity-badge comments">{user.commentsCount} comments</span>
                                                    <span className="user-activity-badge reviews">{user.reviewsCount} reviews</span>
                                                    <span className="user-activity-badge contributions">{user.contributionsCount} contrib</span>
                                                    <span className="user-activity-badge total">{user.totalActivity} total</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </>
                )}

                {/* User Detail Modal */}
                <Modal
                    show={modalOpen}
                    onHide={() => setModalOpen(false)}
                    size="lg"
                    className="user-activity-modal"
                >
                    <Modal.Header closeButton className="user-activity-modal-header">
                        <Modal.Title className="user-activity-modal-title">
                            <FaChartLine /> {selectedUser?.username}'s Activity
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="user-activity-modal-body">
                        {userActivity ? (
                            <div>
                                {/* User Info */}
                                <div className="user-activity-modal-user-info">
                                    {userActivity.user.avatar ? (
                                        <img
                                            src={userActivity.user.avatar}
                                            alt={userActivity.user.username}
                                            className="user-activity-modal-avatar"
                                        />
                                    ) : (
                                        <div className="user-activity-modal-avatar-placeholder">
                                            {getInitials(userActivity.user.username)}
                                        </div>
                                    )}
                                    <div className="user-activity-modal-user-details">
                                        <h5>{userActivity.user.username}</h5>
                                        <p>{userActivity.user.email}</p>
                                        <small>
                                            <FaCalendarAlt /> Joined: {formatDate(userActivity.user.createdAt)} |
                                            <FaCoins /> Points: {userActivity.user.points}
                                        </small>
                                    </div>
                                </div>

                                {/* Stats Grid */}
                                <div className="user-activity-modal-stats">
                                    <div className="user-activity-modal-stat">
                                        <span className="user-activity-modal-stat-value comments">
                                            {userActivity.activity.commentsCount}
                                        </span>
                                        <span className="user-activity-modal-stat-label">
                                            <FaComment /> Comments
                                        </span>
                                    </div>
                                    <div className="user-activity-modal-stat">
                                        <span className="user-activity-modal-stat-value reviews">
                                            {userActivity.activity.reviewsCount}
                                        </span>
                                        <span className="user-activity-modal-stat-label">
                                            <FaStar /> Reviews
                                        </span>
                                    </div>
                                    <div className="user-activity-modal-stat">
                                        <span className="user-activity-modal-stat-value contributions">
                                            {userActivity.activity.contributionsCount}
                                        </span>
                                        <span className="user-activity-modal-stat-label">
                                            <FaHandsHelping /> Contributions
                                        </span>
                                    </div>
                                </div>

                                {/* Activity Tabs */}
                                <div className="user-activity-modal-tabs">
                                    <button
                                        className={`user-activity-modal-tab ${modalTab === 'comments' ? 'active' : ''}`}
                                        onClick={() => setModalTab('comments')}
                                    >
                                        Comments ({userActivity.activity.commentsCount})
                                    </button>
                                    <button
                                        className={`user-activity-modal-tab ${modalTab === 'reviews' ? 'active' : ''}`}
                                        onClick={() => setModalTab('reviews')}
                                    >
                                        Reviews ({userActivity.activity.reviewsCount})
                                    </button>
                                    <button
                                        className={`user-activity-modal-tab ${modalTab === 'contributions' ? 'active' : ''}`}
                                        onClick={() => setModalTab('contributions')}
                                    >
                                        Contributions ({userActivity.activity.contributionsCount})
                                    </button>
                                </div>

                                {/* Activity List */}
                                <div className="user-activity-list">
                                    {modalTab === 'comments' && (
                                        userActivity.activity.comments.length > 0 ? (
                                            userActivity.activity.comments.map((comment, i) => (
                                                <div key={i} className="user-activity-item">
                                                    <p>{comment.comment}</p>
                                                    <small>
                                                        {formatDate(comment.createdAt)} |
                                                        Likes: {comment.likes?.length || 0} |
                                                        Dislikes: {comment.dislikes?.length || 0}
                                                    </small>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-muted text-center py-4">No comments yet.</p>
                                        )
                                    )}

                                    {modalTab === 'reviews' && (
                                        userActivity.activity.reviews.length > 0 ? (
                                            userActivity.activity.reviews.map((review, i) => (
                                                <div key={i} className="user-activity-item">
                                                    <p>{review.reviewBody}</p>
                                                    <small>
                                                        {formatDate(review.createdAt)} |
                                                        Rating: {review.reviewRating?.ratingValue || 'N/A'} |
                                                        Likes: {review.likes?.length || 0}
                                                    </small>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-muted text-center py-4">No reviews yet.</p>
                                        )
                                    )}

                                    {modalTab === 'contributions' && (
                                        userActivity.activity.contributions.length > 0 ? (
                                            userActivity.activity.contributions.map((contrib, i) => (
                                                <div key={i} className="user-activity-item">
                                                    <p><strong>{contrib.songName}</strong></p>
                                                    <small>
                                                        Artist: {contrib.performedBy} |
                                                        Status: <Badge bg={
                                                            contrib.status === 'Approved' ? 'success' :
                                                            contrib.status === 'Rejected' ? 'danger' : 'warning'
                                                        }>{contrib.status}</Badge> |
                                                        {formatDate(contrib.created_at)}
                                                    </small>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-muted text-center py-4">No contributions yet.</p>
                                        )
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="user-activity-loading">
                                <div className="user-activity-loading-spinner"></div>
                                <span className="user-activity-loading-text">Loading activity...</span>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="user-activity-modal-footer">
                        <Button
                            className="user-activity-modal-btn user-activity-modal-btn-secondary"
                            onClick={() => setModalOpen(false)}
                        >
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </CommonLayout>
    );
};

export default UserActivityList;
